/**
 * EmailSendIndicator - Inline email send history indicator.
 * Shows send count and last delivery date with a "view history" link
 * that opens the Send dialog pre-navigated to the History tab.
 *
 * R3 AC5 (SPEC-christa-contacts-identity-2026-10-05): when the invoice page passes the one send rule's answer
 * (`sendEvidence`), a send she recorded herself (Mark as Sent after Outlook) is shown with the fixed phrase
 * "Marked as sent by {name} on {date}", whether or not the Keystone email log has a row. "Not yet sent" is only for an
 * invoice with no recorded send. A status that went back to Ready is said plainly, and "This version has not been sent
 * yet." appears only when the content changed after the send (document history), never for a status change alone.
 */
import { useEmailSendHistory } from '@/hooks/useEmailSendHistory';
import { Mail } from 'lucide-react';
import {
  changedAfterSendLine,
  formatSendDate,
  isSent,
  sendEvidencePhrase,
  type SendEvidence,
} from '@/lib/sendEvidence';

interface EmailSendIndicatorProps {
  entityType: 'invoice' | 'proposal';
  entityId: string;
  onViewHistory: () => void;
  /** The server's decision on whether, when and by whom this invoice was sent (invoices only). */
  sendEvidence?: SendEvidence | null;
  invoiceStatus?: string;
  statusChangedAt?: string | null;
  /** Document history says the content changed after the recorded send. */
  contentChangedAfterSend?: boolean;
}

export function EmailSendIndicator({
  entityType,
  entityId,
  onViewHistory,
  sendEvidence,
  invoiceStatus,
  statusChangedAt,
  contentChangedAfterSend,
}: EmailSendIndicatorProps) {
  const { data, isLoading } = useEmailSendHistory(entityType, entityId);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="email-send-indicator">
        <Mail className="h-3.5 w-3.5" />
        <span>Loading...</span>
      </div>
    );
  }

  const sends = data?.items ?? [];
  const totalSends = data?.total ?? sends.length;

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const viewHistory = (
    <button
      onClick={onViewHistory}
      className="text-blue-600 hover:text-blue-800 hover:underline text-sm font-medium"
      data-testid="view-history-link"
    >
      view history
    </button>
  );

  if (totalSends === 0) {
    if (sendEvidence && isSent(sendEvidence)) {
      // A recorded send with no Keystone email log row: her own Mark as Sent (or a status with no date recorded).
      const phrase = sendEvidencePhrase(sendEvidence);
      const wasBackToDraftOrReady = invoiceStatus === 'draft' || invoiceStatus === 'ready';
      const statusMovedAfterSend = wasBackToDraftOrReady && !!statusChangedAt
        && (!sendEvidence.at || new Date(statusChangedAt).getTime() >= new Date(sendEvidence.at).getTime());
      return (
        <div className="space-y-1 text-sm" data-testid="email-send-indicator">
          <div className="flex flex-wrap items-center gap-2">
            <Mail className="h-3.5 w-3.5 text-muted-foreground" />
            <span data-testid="email-send-recorded">
              {phrase}.
              {statusMovedAfterSend && (
                <> Status changed to {invoiceStatus === 'draft' ? 'Draft' : 'Ready'} on {formatSendDate(statusChangedAt)}.</>
              )}
              {contentChangedAfterSend && <> {changedAfterSendLine(invoiceStatus)}</>}
            </span>
            {viewHistory}
          </div>
        </div>
      );
    }
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="email-send-indicator">
        <Mail className="h-3.5 w-3.5" />
        <span>Not yet sent</span>
      </div>
    );
  }

  // Find the latest delivered send
  const lastDelivered = sends.find(s => s.delivery_status === 'delivered');
  const lastSend = sends[0]; // Most recent send (API returns newest first)

  const statusText = lastDelivered
    ? `Last delivered ${formatDate(lastDelivered.delivery_updated_at || lastDelivered.sent_at)}`
    : lastSend?.delivery_status === 'pending'
      ? 'Delivery pending'
      : lastSend?.delivery_status === 'bounced'
        ? 'Last send bounced'
        : lastSend?.delivery_status === 'failed'
          ? 'Last send failed'
          : '';

  // R3 AC5: with the send rule's answer in hand, a Keystone email is named as one, with its date.
  const keystonePhrase = sendEvidence && lastSend
    ? `Emailed from Keystone on ${formatSendDate(lastSend.sent_at)}. `
    : '';

  return (
    <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="email-send-indicator">
      <Mail className="h-3.5 w-3.5 text-muted-foreground" />
      <span>
        {keystonePhrase}
        Sent {totalSends} {totalSends === 1 ? 'time' : 'times'}
        {statusText && <span className="text-muted-foreground"> &middot; {statusText}</span>}
        {contentChangedAfterSend && <span className="text-muted-foreground"> {changedAfterSendLine(invoiceStatus)}</span>}
      </span>
      {viewHistory}
    </div>
  );
}
